import {Button, Dialog, TextField} from '@wandb/components';
import {TailwindContents} from '@wandb/weave/components/Tailwind';
import React, {useState} from 'react';

import {useRenameRunGroup} from '../../../state/runs/hooks';

type RenameRunGroupDialogProps = {
  closeDialog: () => void;
  entityName: string;
  groupName: string;
  projectName: string;
};

export const RenameRunGroupDialog = ({
  closeDialog,
  entityName,
  groupName,
  projectName,
}: RenameRunGroupDialogProps) => {
  const renameRunGroup = useRenameRunGroup({entityName, projectName});
  const [newName, setNewName] = useState(groupName);
  const [isSaving, setIsSaving] = useState(false);

  // Submitting the current name would rename nothing and then delete the
  // group's sidecar row, losing its notes and tags; an empty name would clear
  // the group from every run in it.
  const trimmedName = newName.trim();
  const canSave = trimmedName !== '' && trimmedName !== groupName;

  const onSave = async () => {
    if (!canSave || isSaving) {
      return;
    }
    setIsSaving(true);
    const renamed = await renameRunGroup(groupName, trimmedName);
    setIsSaving(false);
    if (renamed) {
      closeDialog();
    }
  };

  const onEnterKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      onSave();
    }
  };

  return (
    <Dialog.Modal
      isOpen
      onOpenChange={open => {
        if (!open) {
          closeDialog();
        }
      }}>
      <Dialog.Heading>Edit group name</Dialog.Heading>
      <Dialog.Content>
        <TailwindContents>
          <div className="text-moon-700 dark:text-moon-300">
            Renaming a group moves every run in it, everywhere the group is
            used. If a group with the new name already exists, the two are
            merged.
            <div className="mt-16">
              <TextField
                label="Group name"
                value={newName}
                onChange={setNewName}
                onKeyDown={onEnterKeyDown}
                placeholder="Choose a group name"
                fullWidth
                autoFocus_I_READ_THE_JSDOC
              />
            </div>
          </div>
        </TailwindContents>
      </Dialog.Content>
      <Dialog.Footer>
        <Button isDisabled={!canSave || isSaving} onPress={onSave}>
          Save
        </Button>
        <Button variant="secondary" onPress={closeDialog}>
          Cancel
        </Button>
      </Dialog.Footer>
    </Dialog.Modal>
  );
};
